import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import { shouldActivateCard } from "../core/gestures.js";

gsap.registerPlugin(Flip);

function splitIds(value = "") {
  return value.split(/\s+/).filter(Boolean);
}

function containsFocus(element) {
  return element.contains(document.activeElement);
}

export class PortfolioController {
  #store;
  #scroll;
  #liveRegion;
  #filters = [];
  #groups = [];
  #cards = [];
  #skills = [];
  #skillFilters = [];
  #projectNodes = [];
  #timelineFilters = [];
  #timelineItems = [];
  #filterStatus = null;
  #skillMap = null;
  #skillLines = null;
  #activeSkillLabel = null;
  #guide = null;
  #cleanup = [];
  #guideTimer = 0;
  #filterTween = null;
  #resizeObserver = null;
  #filter = "all";
  #selectedSkill = "all";
  #activeProject = null;
  #reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  constructor({ store, scroll, liveRegion }) {
    this.#store = store;
    this.#scroll = scroll;
    this.#liveRegion = liveRegion;
  }

  init() {
    this.#filters = [...document.querySelectorAll("[data-project-filter]")];
    this.#groups = [...document.querySelectorAll("[data-project-group]")];
    this.#cards = [...document.querySelectorAll("[data-project-id]")];
    this.#skills = [...document.querySelectorAll("[data-skill-id]")];
    this.#skillFilters = [...document.querySelectorAll("[data-skill-filter]")];
    this.#projectNodes = [
      ...document.querySelectorAll("[data-related-project]")
    ];
    this.#timelineFilters = [
      ...document.querySelectorAll("[data-timeline-filter]")
    ];
    this.#timelineItems = [...document.querySelectorAll("[data-timeline-type]")];
    this.#filterStatus = document.querySelector("[data-project-filter-status]");
    this.#skillMap = document.querySelector("[data-skill-map]");
    this.#skillLines = document.querySelector("[data-skill-lines]");
    this.#activeSkillLabel = document.querySelector(
      "[data-active-skill-label]"
    );
    this.#guide = document.querySelector("[data-exploration-guide]");

    this.#initProjectFilters();
    this.#initHeroRoutes();
    this.#initSkillRelationships();
    this.#initProjectCardActivation();
    this.#initSkillMap();
    this.#initTimelineFilters();
    this.#initGuide();
    this.setFilter("all", { announce: false, animate: false });
    this.#store.patch(
      {
        portfolio: {
          filter: "all",
          activeProjectId: null,
          activeSkillId: null
        }
      },
      "portfolio:init"
    );
  }

  #listen(element, eventName, handler, options) {
    element.addEventListener(eventName, handler, options);
    this.#cleanup.push(() =>
      element.removeEventListener(eventName, handler, options)
    );
  }

  #initProjectFilters() {
    this.#filters.forEach((button) => {
      this.#listen(button, "click", () => {
        this.setFilter(button.dataset.projectFilter);
        this.#dismissGuide();
      });
    });
  }

  #initHeroRoutes() {
    document.querySelectorAll("[data-project-route]").forEach((route) => {
      this.#listen(route, "click", (event) => {
        event.preventDefault();
        this.setFilter(route.dataset.projectRoute);
        this.#scroll.scrollTo(document.getElementById("projects"));
        this.#dismissGuide();
      });
    });
  }

  #initSkillRelationships() {
    this.#skills.forEach((skill) => {
      skill.tabIndex = 0;
      skill.setAttribute("role", "button");
      skill.setAttribute("aria-pressed", "false");
      skill.setAttribute("aria-expanded", "false");
      const ids = splitIds(skill.dataset.projectIds);

      this.#listen(skill, "pointerenter", () => {
        this.#highlightProjects(ids, skill.dataset.skillId);
      });
      this.#listen(skill, "pointerleave", () => {
        if (!containsFocus(skill) && this.#selectedSkill === "all") {
          this.#clearRelationshipHighlights();
        }
      });
      this.#listen(skill, "focusin", () => {
        this.#highlightProjects(ids, skill.dataset.skillId);
      });
      this.#listen(skill, "focusout", () => {
        window.setTimeout(() => {
          if (!containsFocus(skill) && this.#selectedSkill === "all") {
            this.#clearRelationshipHighlights();
          }
        }, 0);
      });
      this.#listen(skill, "click", () => {
        const next =
          this.#selectedSkill === skill.dataset.skillId
            ? "all"
            : skill.dataset.skillId;
        this.#selectSkill(next);
      });
      this.#listen(skill, "keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          skill.click();
        }
      });
    });

    this.#cards.forEach((card) => {
      const projectId = card.dataset.projectId;
      this.#listen(card, "pointerenter", () => {
        this.#activeProject = projectId;
        this.#highlightSkills(projectId);
      });
      this.#listen(card, "pointerleave", () => {
        if (!containsFocus(card)) {
          this.#activeProject = null;
          this.#restoreSelectedSkill();
        }
      });
      this.#listen(card, "focusin", () => {
        this.#activeProject = projectId;
        this.#highlightSkills(projectId);
      });
      this.#listen(card, "focusout", () => {
        window.setTimeout(() => {
          if (!containsFocus(card)) {
            this.#activeProject = null;
            this.#restoreSelectedSkill();
          }
        }, 0);
      });
    });

    this.#skillFilters.forEach((button) => {
      this.#listen(button, "click", () => {
        this.#selectSkill(button.dataset.skillFilter);
      });
    });
  }

  #initProjectCardActivation() {
    this.#cards.forEach((card) => {
      let start = null;
      this.#listen(card, "pointerdown", (event) => {
        if (event.button !== 0 || event.target.closest("button, a, summary")) {
          start = null;
          return;
        }
        start = { x: event.clientX, y: event.clientY };
        card.classList.add("is-pressed");
      });
      this.#listen(card, "pointercancel", () => {
        start = null;
        card.classList.remove("is-pressed");
      });
      this.#listen(card, "pointerleave", () => {
        card.classList.remove("is-pressed");
      });
      this.#listen(card, "pointerup", (event) => {
        card.classList.remove("is-pressed");
        if (!start || event.target.closest("button, a, summary, details")) {
          start = null;
          return;
        }
        const selectionActive = Boolean(window.getSelection()?.toString());
        const activate = shouldActivateCard(
          {
            startX: start.x,
            startY: start.y,
            endX: event.clientX,
            endY: event.clientY,
            selectionActive
          },
          { movementTolerance: 8 }
        );
        start = null;
        if (!activate) {
          return;
        }
        card.querySelector("[data-open-project]")?.click();
        this.#dismissGuide();
      });
    });
  }

  #initSkillMap() {
    if (!this.#skillMap || !this.#skillLines) {
      return;
    }
    this.#projectNodes.forEach((node) => {
      this.#listen(node, "pointerenter", () => {
        this.#activeProject = node.dataset.relatedProject;
        this.#highlightSkills(node.dataset.relatedProject);
      });
      this.#listen(node, "pointerleave", () => {
        if (!containsFocus(node)) {
          this.#activeProject = null;
          this.#restoreSelectedSkill();
        }
      });
      this.#listen(node, "focusin", () => {
        this.#activeProject = node.dataset.relatedProject;
        this.#highlightSkills(node.dataset.relatedProject);
      });
      this.#listen(node, "focusout", () => {
        window.setTimeout(() => {
          if (!containsFocus(node)) {
            this.#activeProject = null;
            this.#restoreSelectedSkill();
          }
        }, 0);
      });
      this.#listen(node, "click", (event) => {
        event.preventDefault();
        const projectId = node.dataset.relatedProject;
        const card = this.#cards.find(
          (item) => item.dataset.projectId === projectId
        );
        card?.querySelector("[data-open-project]")?.click();
      });
    });

    const draw = () => this.#drawSkillLines();
    this.#resizeObserver = new ResizeObserver(draw);
    this.#resizeObserver.observe(this.#skillMap);
    this.#skills.forEach((skill) => this.#resizeObserver.observe(skill));
    this.#projectNodes.forEach((node) => this.#resizeObserver.observe(node));
    const frame = window.requestAnimationFrame(draw);
    this.#cleanup.push(() => {
      window.cancelAnimationFrame(frame);
      this.#resizeObserver?.disconnect();
    });
  }

  #drawSkillLines() {
    if (!this.#skillMap || !this.#skillLines) {
      return;
    }
    const mapRect = this.#skillMap.getBoundingClientRect();
    if (!mapRect.width || !mapRect.height) {
      return;
    }
    this.#skillLines.setAttribute(
      "viewBox",
      `0 0 ${mapRect.width} ${mapRect.height}`
    );
    this.#skillLines.replaceChildren();
    const namespace = "http://www.w3.org/2000/svg";
    this.#skills.forEach((skill) => {
      const skillRect = skill.getBoundingClientRect();
      const startX = skillRect.left - mapRect.left + skillRect.width / 2;
      const startY = skillRect.bottom - mapRect.top;
      splitIds(skill.dataset.projectIds).forEach((projectId) => {
        const project = this.#projectNodes.find(
          (node) => node.dataset.relatedProject === projectId
        );
        if (!project) {
          return;
        }
        const projectRect = project.getBoundingClientRect();
        const endX =
          projectRect.left - mapRect.left + projectRect.width / 2;
        const endY = projectRect.top - mapRect.top;
        const distance = Math.max(40, Math.abs(endY - startY));
        const path = document.createElementNS(namespace, "path");
        path.dataset.lineSkill = skill.dataset.skillId;
        path.dataset.lineProject = projectId;
        path.setAttribute(
          "d",
          `M ${startX} ${startY} C ${startX} ${startY + distance * 0.42}, ${endX} ${endY - distance * 0.42}, ${endX} ${endY}`
        );
        this.#skillLines.append(path);
      });
    });
    this.#syncRelationshipLines();
  }

  #syncRelationshipLines({ skillId = null, projectId = null } = {}) {
    if (!this.#skillLines) {
      return;
    }
    const hasFocus = Boolean(skillId || projectId);
    this.#skillLines.querySelectorAll("path").forEach((path) => {
      const related =
        (skillId && path.dataset.lineSkill === skillId) ||
        (projectId && path.dataset.lineProject === projectId);
      path.classList.toggle(
        "is-related",
        Boolean(hasFocus && related)
      );
      path.classList.toggle(
        "is-dimmed",
        Boolean(hasFocus && !related)
      );
    });
  }

  #highlightProjects(projectIds, skillId) {
    const related = new Set(projectIds);
    this.#cards.forEach((card) => {
      const isRelated = related.has(card.dataset.projectId);
      card.classList.toggle("is-related", isRelated);
      card.classList.toggle("is-dimmed", !isRelated);
    });
    this.#projectNodes.forEach((node) => {
      const isRelated = related.has(node.dataset.relatedProject);
      node.classList.toggle("is-related", isRelated);
      node.classList.toggle("is-dimmed", !isRelated);
    });
    this.#skills.forEach((skill) => {
      skill.classList.toggle("is-related", skill.dataset.skillId === skillId);
    });
    this.#syncRelationshipLines({ skillId });
  }

  #highlightSkills(projectId) {
    this.#cards.forEach((card) => {
      card.classList.toggle("is-related", card.dataset.projectId === projectId);
      card.classList.remove("is-dimmed");
    });
    this.#skills.forEach((skill) => {
      const isRelated = splitIds(skill.dataset.projectIds).includes(projectId);
      skill.classList.toggle("is-related", isRelated);
      skill.classList.toggle("is-dimmed", !isRelated);
    });
    this.#projectNodes.forEach((node) => {
      const active = node.dataset.relatedProject === projectId;
      node.classList.toggle("is-related", active);
      node.classList.toggle("is-dimmed", !active);
    });
    this.#syncRelationshipLines({ projectId });
    this.#store.patch(
      { portfolio: { activeProjectId: projectId } },
      "portfolio:project-focus"
    );
  }

  #clearRelationshipHighlights() {
    [...this.#cards, ...this.#skills, ...this.#projectNodes].forEach(
      (element) => {
      element.classList.remove("is-related", "is-dimmed");
      }
    );
    this.#syncRelationshipLines();
  }

  #restoreSelectedSkill() {
    if (this.#selectedSkill === "all") {
      this.#clearRelationshipHighlights();
      this.#store.patch(
        { portfolio: { activeProjectId: null } },
        "portfolio:project-blur"
      );
      return;
    }
    const skill = this.#skills.find(
      (item) => item.dataset.skillId === this.#selectedSkill
    );
    if (skill) {
      this.#highlightProjects(
        splitIds(skill.dataset.projectIds),
        skill.dataset.skillId
      );
    }
  }

  #selectSkill(id) {
    const valid =
      id === "all" ||
      this.#skills.some((skill) => skill.dataset.skillId === id);
    if (!valid) {
      return false;
    }
    this.#selectedSkill = id;
    this.#skills.forEach((skill) => {
      const selected = skill.dataset.skillId === id;
      skill.classList.toggle("is-selected", selected);
      skill.setAttribute("aria-pressed", String(selected));
      skill.setAttribute("aria-expanded", String(selected));
    });
    this.#skillFilters.forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.skillFilter === id)
      );
    });

    if (id === "all") {
      this.#clearRelationshipHighlights();
      if (this.#activeSkillLabel) {
        this.#activeSkillLabel.textContent = `全部能力 · ${this.#projectNodes.length} 个项目`;
      }
      this.#liveRegion.textContent = "已显示全部能力与项目关联。";
    } else {
      const skill = this.#skills.find(
        (item) => item.dataset.skillId === id
      );
      const projectIds = splitIds(skill.dataset.projectIds);
      const title = skill.querySelector("h3")?.textContent ?? "所选能力";
      this.#highlightProjects(projectIds, id);
      if (this.#activeSkillLabel) {
        this.#activeSkillLabel.textContent =
          `${title} · ${projectIds.length} 个关联项目`;
      }
      this.#liveRegion.textContent = `已聚焦${title}相关项目。`;
    }
    this.#store.patch(
      { portfolio: { activeSkillId: id === "all" ? null : id } },
      "portfolio:skill-filter"
    );
    return true;
  }

  setActiveSkill(id) {
    return this.#selectSkill(id);
  }

  getActiveSkill() {
    return this.#selectedSkill === "all" ? null : this.#selectedSkill;
  }

  #initTimelineFilters() {
    this.#timelineItems.forEach((item) => {
      const details = item.querySelector("details");
      if (!details) {
        return;
      }
      this.#listen(details, "toggle", () => {
        item.classList.toggle("is-open", details.open);
        if (!details.open) {
          return;
        }
        this.#timelineItems.forEach((otherItem) => {
          const otherDetails = otherItem.querySelector("details");
          if (otherDetails && otherDetails !== details) {
            otherDetails.open = false;
          }
        });
        const title =
          details.querySelector("strong")?.textContent ?? "成长轨迹";
        this.#liveRegion.textContent = `已展开${title}。`;
      });
    });

    this.#timelineFilters.forEach((button) => {
      this.#listen(button, "click", () => {
        const value = button.dataset.timelineFilter;
        this.#timelineFilters.forEach((item) => {
          item.setAttribute(
            "aria-pressed",
            String(item.dataset.timelineFilter === value)
          );
        });
        this.#timelineItems.forEach((item) => {
          const visible =
            value === "all" || item.dataset.timelineType === value;
          item.hidden = !visible;
          item.setAttribute("aria-hidden", String(!visible));
          const details = item.querySelector("details");
          if (!visible && details) {
            details.open = false;
          }
        });
        this.#liveRegion.textContent =
          value === "all"
            ? "已显示全部成长轨迹。"
            : value === "education"
              ? "已显示教育经历。"
              : "已显示奖项与认证。";
      });
    });
  }

  #initGuide() {
    if (!this.#guide) {
      return;
    }
    const closeButton = this.#guide.querySelector("[data-close-guide]");
    if (closeButton) {
      this.#listen(closeButton, "click", () => this.#dismissGuide());
    }
    const windButton = document.querySelector("#wind-control");
    if (windButton) {
      this.#listen(windButton, "click", () => this.#dismissGuide());
    }
    this.#guideTimer = window.setTimeout(() => this.#dismissGuide(), 9000);
  }

  #dismissGuide() {
    if (!this.#guide || this.#guide.classList.contains("is-dismissed")) {
      return;
    }
    window.clearTimeout(this.#guideTimer);
    this.#guide.classList.add("is-dismissed");
    window.setTimeout(() => {
      if (this.#guide) {
        this.#guide.hidden = true;
      }
    }, 500);
  }

  setFilter(id, { announce = true, animate = true } = {}) {
    const valid =
      id === "all" ||
      this.#groups.some((group) => group.dataset.projectGroup === id);
    if (!valid) {
      return false;
    }
    const shouldAnimate =
      animate &&
      !this.#reduced &&
      this.#filter !== id &&
      this.#groups.some((group) => !group.hidden);
    const flipState = shouldAnimate
      ? Flip.getState(this.#groups, { props: "opacity" })
      : null;
    this.#filter = id;
    this.#filters.forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.projectFilter === id)
      );
    });
    this.#groups.forEach((group) => {
      const visible =
        id === "all" || group.dataset.projectGroup === id;
      group.hidden = !visible;
      group.setAttribute("aria-hidden", String(!visible));
    });
    this.#cards.forEach((card) => {
      const visible =
        id === "all" || card.dataset.projectCategory === id;
      card.hidden = !visible;
      card.setAttribute("aria-hidden", String(!visible));
      if ("inert" in card) {
        card.inert = !visible;
      }
    });
    const label =
      this.#filters.find((button) => button.dataset.projectFilter === id)
        ?.textContent.trim() ?? "全部";
    const visibleCount = this.#cards.filter(
      (card) => id === "all" || card.dataset.projectCategory === id
    ).length;
    if (this.#filterStatus) {
      this.#filterStatus.textContent =
        `${String(visibleCount).padStart(2, "0")} PROJECTS · ${label}`;
    }
    if (flipState) {
      const section = document.querySelector('[data-section="projects"]');
      section?.classList.add("is-filtering");
      this.#filterTween?.kill();
      this.#store.patch(
        { interaction: { filtering: true } },
        "interaction:filter-start"
      );
      this.#filterTween = Flip.from(flipState, {
        duration: 0.58,
        ease: "power2.inOut",
        absolute: true,
        fade: true,
        prune: true,
        onEnter: (elements) =>
          gsap.fromTo(
            elements,
            { opacity: 0, y: 22 },
            { opacity: 1, y: 0, duration: 0.42, ease: "power2.out" }
          ),
        onLeave: (elements) =>
          gsap.to(elements, {
            opacity: 0,
            y: -18,
            duration: 0.28,
            ease: "power1.in"
          }),
        onComplete: () => {
          section?.classList.remove("is-filtering");
          this.#store.patch(
            { interaction: { filtering: false } },
            "interaction:filter-end"
          );
        }
      });
    }
    this.#store.patch(
      { portfolio: { filter: id } },
      "portfolio:project-filter"
    );
    if (announce) {
      this.#liveRegion.textContent =
        `已显示${label}项目，共 ${visibleCount} 个。`;
    }
    return true;
  }

  getFilter() {
    return this.#filter;
  }

  getActiveProject() {
    return this.#activeProject;
  }

  destroy() {
    this.#cleanup.forEach((cleanup) => cleanup());
    window.clearTimeout(this.#guideTimer);
    this.#filterTween?.kill();
    this.#resizeObserver?.disconnect();
    this.#clearRelationshipHighlights();
  }
}
